import { DataGrid, GridActionsCellItem, GridCellParams, GridColDef } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { IRootState } from '../../../../../../redux/types'
import useScreenWidth from '../../../../../shared/hooks/useScreenWidth'

import { useTranslation } from 'react-i18next'
import CustomToolbar from './CustomToolbar'
import NoveltiesLanguagesCell from './NoveltiesLanguagesCell'
import NoveltiesTagsCell from './NoveltiesTagsCell'
import NoveltiesTitleCell from './NoveltiesTitleCell'
import { useNavigate } from 'react-router'

interface NoveltiesDataGridProps {
  novelties: any[]
  tableclass: any
  loading: boolean
  onHighlight: (id: number, isHighlight: boolean) => void
  onEdit: (id: number) => void
  onDelete: (id: number) => void
}

const NoveltiesDataGrid = ({ novelties, loading, onHighlight, onEdit, onDelete }: NoveltiesDataGridProps) => {
  const { t } = useTranslation(['global'])
  const navigate = useNavigate()
  const isMobile = useScreenWidth(700)
  const { globalBrandId } = useSelector((state: IRootState) => state.global)

  const getColumns = (): GridColDef[] => {
    return [
      {
        field: 'title',
        headerName: t('title'),
        flex: 1,
        renderCell: (params: GridCellParams) => <NoveltiesTitleCell params={params} onHighlight={onHighlight} />
      },
      {
        field: 'tags',
        headerName: '',
        width: 90,
        maxWidth: 240,
        renderCell: (params: GridCellParams) => <NoveltiesTagsCell params={params} />,
        hideable: isMobile
      },
      {
        field: 'id',
        headerName: 'Id',
        width: 70,
        hideable: isMobile
      },
      {
        field: 'brand',
        headerName: t('brand'),
        width: 90,
        maxWidth: 120,
        valueGetter: (params: GridCellParams) => params.row.brand?.name || '-',
        hideable: globalBrandId !== null
      },
      {
        field: 'language',
        headerName: t('language'),
        width: 120,
        renderCell: (params: GridCellParams) => <NoveltiesLanguagesCell params={params} />,
        hideable: isMobile
      },
      {
        field: 'role',
        headerName: t('roles'),
        width: 120,
        valueGetter: (params: GridCellParams) => params.row.role?.name || '-',
        hideable: isMobile
      },
      {
        field: 'created_at',
        headerName: t('date'),
        width: 150,
        valueGetter: (params: GridCellParams) => {
          if (params.row.created_at !== null) {
            return dayjs(params.row.created_at).format('DD MMM YYYY')
          }
          return '--'
        },
        hideable: isMobile
      },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 90,
      getActions: (params: GridCellParams) => [
        <GridActionsCellItem
          label={t('view')}
          onClick={() => navigate(`${params.row.slug}`)}
          showInMenu
        />,
        <GridActionsCellItem
          label={t('edit')}
          onClick={() => onEdit(params.id as number)}
          showInMenu
        />,
        <GridActionsCellItem
          label={t('delete')}
          onClick={() => onDelete(params.id as number)}
          showInMenu
        />
      ]
    }
    ].filter(columns => !(isMobile && ['id', 'tags', 'language', 'role', 'created_at'].includes(columns.field)))
  }

  const columns = getColumns()

  return (
    <DataGrid
      rows={novelties}
      columns={columns}
      pageSizeOptions={[5, 10, 20]}
      loading={loading}
      slots={{ toolbar: CustomToolbar }}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 10 }
        }
      }}
      autoHeight
      density="comfortable"
      disableRowSelectionOnClick
    />
  )
}

export default NoveltiesDataGrid
